import { Component, inject, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { ConnectivityService, VersionCheckService } from '@capturum/complete';
import { CSSVarNames, ThemeService, ValidatorService } from '@capturum/ui/api';
import { CapturumButtonModule } from '@capturum/ui/button';
import { CapturumToastModule } from '@capturum/ui/toast';
import { environment } from '@environments/environment';
import { TranslateModule } from '@ngx-translate/core';
import { NeqtoAuthService } from '@shared/services/neqto-auth.service';
import { OverlayDismissService } from '@shared/services/overlay-dismiss.service';
import { Confirmation, ConfirmationService, SharedModule } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { combineLatest, filter, skipWhile, switchMap } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [VersionCheckService],
  imports: [
    RouterOutlet,
    CapturumToastModule,
    ConfirmDialogModule,
    SharedModule,
    CapturumButtonModule,
    TranslateModule,
  ],
})
export class AppComponent implements OnInit {
  confirmation: Confirmation;

  readonly #versionCheckService = inject(VersionCheckService);
  readonly #themeService = inject(ThemeService);
  readonly #confirmationService = inject(ConfirmationService);
  readonly #validatorService = inject(ValidatorService);
  readonly #authService = inject(NeqtoAuthService);
  readonly #connecitivityService = inject(ConnectivityService);
  readonly #overlayDismissService = inject(OverlayDismissService);

  constructor() {
    this.#validatorService.setValidationMessages({
      url: 'validation-errors.url.valid',
      version: 'validation-errors.semver.valid',
      dtoKey: 'formly.validation.closing_brackets',
      dtoKeyArray: 'formly.validation.array.key.format',
      fieldMatch: 'auth.reset-password.mismatch.message',
      ip: 'formly.validation.ip',
      emailOrTemplateVariable: 'formly.validation.email.or.template.variable',
    });
  }

  ngOnInit(): void {
    this.#fetchVersionChanging();
    this.#setTheme();
    this.#listenToWindowFocus();
    this.#overlayDismissService.init();

    this.#confirmationService.requireConfirmation$.subscribe((result) => {
      return (this.confirmation = result);
    });
  }

  #setTheme(): void {
    this.#themeService.setProps({
      [CSSVarNames.Primary]: '#10b981',
      [CSSVarNames.Success]: '#10b981',
      [CSSVarNames.Warning]: '#b45309',
      [CSSVarNames.Error]: '#dc2626',
    });
  }

  #fetchVersionChanging(): void {
    this.#versionCheckService.initVersionCheck(`${environment.url}/version.json`, 10000);

    this.#versionCheckService.onUpdateVersion.subscribe(() => {
      window.location.reload();
    });
  }

  #listenToWindowFocus(): void {
    combineLatest([this.#authService.getWindowFocus(), this.#connecitivityService.isOnline()])
      .pipe(
        skipWhile(() => {
          return !this.#authService.isAuthenticated();
        }),
        filter(([focus, online]) => {
          return focus && online;
        }),
        switchMap((focusOnline) => {
          return this.#authService.checkJwtToken();
        }),
      )
      .subscribe();
  }
}
